import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Navratri Canvas Component
function NavratriCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let dandiyaSticks = [];
            let garbaCircles = [];
            let diyas = [];
            let sparkles = [];
            let textParticles = [];

            // Navratri color palette
            const navratriColors = [
                '#FF1493', // Hot Pink
                '#FFD700', // Gold
                '#FF6347', // Red
                '#32CD32', // Green
                '#FF4500', // Orange Red
                '#9370DB', // Purple
                '#00CED1', // Turquoise
                '#FF69B4', // Pink
                '#FFA500', // Orange
            ];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // Helper: Create Dandiya Stick
            const createDandiyaStick = (x, y) => ({
                x,
                y,
                length: 80,
                width: 8,
                angle: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.1,
                colors: [
                    navratriColors[Math.floor(Math.random() * navratriColors.length)],
                    navratriColors[Math.floor(Math.random() * navratriColors.length)],
                ],
                floatOffset: Math.random() * Math.PI * 2,
                floatSpeed: 0.02,
                alpha: 0,
                targetAlpha: 0.8,
            });

            // Helper: Update Dandiya Stick
            const updateDandiyaStick = (stick) => {
                stick.angle += stick.rotationSpeed;
                stick.floatOffset += stick.floatSpeed;
                if (stick.alpha < stick.targetAlpha) {
                    stick.alpha += 0.02;
                }
            };

            // Helper: Draw Dandiya Stick
            const drawDandiyaStick = (stick) => {
                ctx.save();
                ctx.globalAlpha = stick.alpha;
                ctx.translate(stick.x, stick.y + Math.sin(stick.floatOffset) * 10);
                ctx.rotate(stick.angle);

                // Draw stick with stripes
                const segments = 6;
                const segmentLength = stick.length / segments;

                for (let i = 0; i < segments; i += 1) {
                    const startX = -stick.length / 2 + i * segmentLength;
                    const color = i % 2 === 0 ? stick.colors[0] : stick.colors[1];

                    ctx.fillStyle = color;
                    ctx.shadowBlur = 10;
                    ctx.shadowColor = color;
                    ctx.fillRect(startX, -stick.width / 2, segmentLength, stick.width);
                }

                // Draw decorative caps
                ctx.beginPath();
                ctx.arc(-stick.length / 2, 0, stick.width / 2 + 2, 0, Math.PI * 2);
                ctx.fillStyle = '#FFD700';
                ctx.fill();

                ctx.beginPath();
                ctx.arc(stick.length / 2, 0, stick.width / 2 + 2, 0, Math.PI * 2);
                ctx.fillStyle = '#FFD700';
                ctx.fill();

                ctx.restore();
            };

            // Helper: Create Garba Circle
            const createGarbaCircle = (x, y, radius) => ({
                x,
                y,
                radius,
                angle: 0,
                rotationSpeed: 0.02,
                color: navratriColors[Math.floor(Math.random() * navratriColors.length)],
                petals: 8,
                alpha: 0.6,
            });

            // Helper: Update Garba Circle
            const updateGarbaCircle = (circle) => {
                circle.angle += circle.rotationSpeed;
            };

            // Helper: Draw Garba Circle
            const drawGarbaCircle = (circle) => {
                ctx.save();
                ctx.globalAlpha = circle.alpha;
                ctx.translate(circle.x, circle.y);
                ctx.rotate(circle.angle);

                // Draw decorative garba pattern
                for (let i = 0; i < circle.petals; i += 1) {
                    const angle = (Math.PI * 2 * i) / circle.petals;
                    const x = Math.cos(angle) * circle.radius;
                    const y = Math.sin(angle) * circle.radius;

                    ctx.beginPath();
                    ctx.arc(x, y, 8, 0, Math.PI * 2);
                    ctx.fillStyle = circle.color;
                    ctx.shadowBlur = 15;
                    ctx.shadowColor = circle.color;
                    ctx.fill();

                    // Inner petals
                    const innerX = Math.cos(angle) * (circle.radius / 2);
                    const innerY = Math.sin(angle) * (circle.radius / 2);
                    ctx.beginPath();
                    ctx.arc(innerX, innerY, 5, 0, Math.PI * 2);
                    ctx.fillStyle = '#FFD700';
                    ctx.fill();
                }

                // Center
                ctx.beginPath();
                ctx.arc(0, 0, 12, 0, Math.PI * 2);
                ctx.fillStyle = '#FFD700';
                ctx.shadowBlur = 20;
                ctx.shadowColor = '#FFD700';
                ctx.fill();

                ctx.restore();
            };

            // Helper: Create Diya
            const createDiya = (x, y) => ({
                x,
                y,
                flameHeight: 20,
                flameWidth: 12,
                flicker: 0,
                flickerSpeed: 0.1,
                glowSize: 30,
            });

            // Helper: Update Diya
            const updateDiya = (diya) => {
                diya.flicker += diya.flickerSpeed;
            };

            // Helper: Draw Diya
            const drawDiya = (diya) => {
                ctx.save();

                // Diya glow
                const gradient = ctx.createRadialGradient(diya.x, diya.y - 10, 0, diya.x, diya.y - 10, diya.glowSize);
                gradient.addColorStop(0, 'rgba(255, 200, 0, 0.8)');
                gradient.addColorStop(0.5, 'rgba(255, 100, 0, 0.4)');
                gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(diya.x, diya.y - 10, diya.glowSize, 0, Math.PI * 2);
                ctx.fill();

                // Flame
                const flameFlicker = Math.sin(diya.flicker) * 3;
                ctx.beginPath();
                ctx.moveTo(diya.x, diya.y);
                ctx.quadraticCurveTo(
                    diya.x - diya.flameWidth / 2,
                    diya.y - diya.flameHeight / 2 + flameFlicker,
                    diya.x,
                    diya.y - diya.flameHeight + flameFlicker
                );
                ctx.quadraticCurveTo(
                    diya.x + diya.flameWidth / 2,
                    diya.y - diya.flameHeight / 2 + flameFlicker,
                    diya.x,
                    diya.y
                );
                ctx.closePath();

                const flameGradient = ctx.createLinearGradient(diya.x, diya.y, diya.x, diya.y - diya.flameHeight);
                flameGradient.addColorStop(0, '#FF4500');
                flameGradient.addColorStop(0.5, '#FFA500');
                flameGradient.addColorStop(1, '#FFD700');

                ctx.fillStyle = flameGradient;
                ctx.shadowBlur = 20;
                ctx.shadowColor = '#FFA500';
                ctx.fill();

                // Diya base
                ctx.beginPath();
                ctx.ellipse(diya.x, diya.y, 15, 6, 0, 0, Math.PI * 2);
                ctx.fillStyle = '#8B4513';
                ctx.shadowBlur = 0;
                ctx.fill();

                ctx.restore();
            };

            // Helper: Create Sparkle
            const createSparkle = () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 3 + 1,
                alpha: Math.random(),
                fadeSpeed: (Math.random() - 0.5) * 0.02,
                color: navratriColors[Math.floor(Math.random() * navratriColors.length)],
            });

            // Helper: Update Sparkle
            const updateSparkle = (sparkle) => {
                sparkle.alpha += sparkle.fadeSpeed;
                if (sparkle.alpha <= 0 || sparkle.alpha >= 1) {
                    sparkle.fadeSpeed *= -1;
                }
            };

            // Helper: Draw Sparkle
            const drawSparkle = (sparkle) => {
                ctx.save();
                ctx.globalAlpha = Math.abs(sparkle.alpha);
                ctx.fillStyle = sparkle.color;
                ctx.shadowBlur = 10;
                ctx.shadowColor = sparkle.color;

                // Draw star
                ctx.beginPath();
                for (let i = 0; i < 4; i += 1) {
                    const angle = (Math.PI * i) / 2;
                    const x = sparkle.x + Math.cos(angle) * sparkle.size;
                    const y = sparkle.y + Math.sin(angle) * sparkle.size;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.fill();

                ctx.restore();
            };

            // Helper: Create Text Particle
            const createTextParticle = (x, y, char, index) => ({
                x,
                y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
                rotation: 0,
                rotationSpeed: 0.05,
                color: navratriColors[index % navratriColors.length],
                pulsePhase: index * 0.5,
                pulseSpeed: 0.08,
            });

            // Helper: Update Text Particle
            const updateTextParticle = (tp) => {
                tp.time += 1;
                if (tp.time > tp.delay) {
                    tp.alpha = Math.min(1, tp.alpha + 0.15);
                    tp.scale = Math.min(1, tp.scale + 0.15);
                    tp.rotation += tp.rotationSpeed;
                    tp.pulsePhase += tp.pulseSpeed;
                }
            };

            // Helper: Draw Text Particle
            const drawTextParticle = (tp) => {
                if (tp.time < tp.delay) return;

                const pulse = Math.sin(tp.pulsePhase) * 0.1 + 1;

                ctx.save();
                ctx.globalAlpha = tp.alpha;
                ctx.translate(tp.x, tp.y);
                ctx.rotate(Math.sin(tp.rotation) * 0.1);
                ctx.scale(tp.scale * pulse, tp.scale * pulse);

                ctx.font = 'bold 50px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Outer glow
                ctx.shadowBlur = 25;
                ctx.shadowColor = tp.color;

                // Stroke
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 3;
                ctx.strokeText(tp.char, 0, 0);

                // Fill
                ctx.fillStyle = tp.color;
                ctx.fillText(tp.char, 0, 0);

                ctx.restore();
            };

            // Action: Initialize elements
            const initializeElements = () => {
                // Dandiya Sticks
                dandiyaSticks = [];
                const stickCount = canvas.width < 768 ? 4 : 8;
                for (let i = 0; i < stickCount; i += 1) {
                    const x = (canvas.width / (stickCount + 1)) * (i + 1);
                    const y = canvas.height * 0.6 + Math.random() * 100;
                    dandiyaSticks.push(createDandiyaStick(x, y));
                }

                // Garba Circles
                garbaCircles = [];
                const circlePositions = canvas.width < 768
                    ? [{ x: canvas.width / 2, y: canvas.height * 0.5, r: 60 }]
                    : [
                        { x: canvas.width * 0.25, y: canvas.height * 0.55, r: 70 },
                        { x: canvas.width * 0.75, y: canvas.height * 0.55, r: 70 },
                    ];
                circlePositions.forEach((pos) => {
                    garbaCircles.push(createGarbaCircle(pos.x, pos.y, pos.r));
                });

                // Diyas
                diyas = [];
                const diyaCount = canvas.width < 768 ? 3 : 6;
                for (let i = 0; i < diyaCount; i += 1) {
                    const x = (canvas.width / (diyaCount + 1)) * (i + 1);
                    const y = canvas.height * 0.85;
                    diyas.push(createDiya(x, y));
                }

                // Sparkles
                sparkles = [];
                for (let i = 0; i < 50; i += 1) {
                    sparkles.push(createSparkle());
                }
            };

            // Action: Create Text
            const createNavratriText = () => {
                const text = 'HAPPY NAVRATRI';

                // Position text responsive to width
                // For Navratri, requested style is similar to Lohri/Holi: Right side on desktop
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.20; // Lower slightly to 20%

                const charSpacing = canvas.width < 768 ? 28 : 40;
                const startX = centerX - (text.length * charSpacing) / 2;

                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const char = text[i];
                    const charX = startX + i * charSpacing;
                    textParticles.push(createTextParticle(charX, centerY, char, i));
                }
            };

            initializeElements();

            setTimeout(() => {
                createNavratriText();
            }, 50);

            // Animation loop
            const animate = () => {
                ctx.fillStyle = 'rgba(10, 0, 30, 0.15)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Update and draw sparkles
                sparkles.forEach((sparkle) => {
                    updateSparkle(sparkle);
                    drawSparkle(sparkle);
                });

                // Update and draw garba circles
                garbaCircles.forEach((circle) => {
                    updateGarbaCircle(circle);
                    drawGarbaCircle(circle);
                });

                // Update and draw dandiya sticks
                dandiyaSticks.forEach((stick) => {
                    updateDandiyaStick(stick);
                    drawDandiyaStick(stick);
                });

                // Update and draw diyas
                diyas.forEach((diya) => {
                    updateDiya(diya);
                    drawDiya(diya);
                });

                // Update and draw text
                textParticles.forEach((tp) => {
                    updateTextParticle(tp);
                    drawTextParticle(tp);
                });

                animationFrameId = requestAnimationFrame(animate);
            };

            animate();

            // Recreate elements on resize
            const handleResize = () => {
                resizeCanvas();
                initializeElements();
                if (textParticles.length > 0) {
                    createNavratriText();
                }
            };
            window.addEventListener('resize', handleResize);

            // Cleanup
            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

export default function NavratriTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            backgroundImage: 'url(/assets/background/festival_navratri_bg.png)',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
            overflow: 'hidden',
            '&:before': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                background: `linear-gradient(135deg, ${alpha('#4B0082', 0.65)}, ${alpha('#8B008B', 0.55)})`,
                zIndex: 0,
            },
        }}>
            <NavratriCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(10,0,20,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha('#FF1493', 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha('#FF1493', 0.2)}`,
                    `0 16px 64px ${alpha('#9370DB', 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: 'linear-gradient(90deg, transparent, #FF1493, #FFD700, #9370DB, transparent)',
                    borderRadius: '0 0 4px 4px',
                    animation: 'navGlow 3s ease-in-out infinite',
                },
                '@keyframes navGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: 'linear-gradient(90deg, #FF1493, #FFD700, #9370DB, #FF1493)',
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha('#FF1493', 0.5)}`,
                    }}>Happy Navratri</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2 }}><Logo /></Stack>
                {cloneElement(children, { hideHelpLink: true })}
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha('#9370DB', 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

NavratriTheme.propTypes = {
    children: PropTypes.node,
};
